"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useRegion } from "@/components/RegionProvider";
import { knownRegions } from "@/lib/location-data";
import { fixMaybeBrokenText } from "@/lib/text-client";

const TEXT = {
  chooseCity: "Выберите город",
  pickup: "Самовывоз и доставка",
  details: "Подробнее",
  current: "Текущий город"
} as const;

function ChevronIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16">
      <path
        d="m4.5 6.5 3.5 3.5 3.5-3.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export function HeaderRegionSelector({ className = "" }: { className?: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { region, setRegion } = useRegion();
  const [isOpen, setIsOpen] = useState(false);
  const regionLabel = fixMaybeBrokenText(region.label || region.city);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div
      className={`region-selector ${isOpen ? "region-selector--open" : ""} ${className}`.trim()}
      ref={containerRef}
    >
      <button
        aria-expanded={isOpen}
        className="region-selector-button"
        onClick={() => setIsOpen((current) => !current)}
        type="button"
      >
        <span className="region-badge">
          <span aria-hidden="true" className="region-badge-pin" />
          <strong>{regionLabel}</strong>
        </span>
        <span aria-hidden="true" className="region-selector-chevron">
          <ChevronIcon />
        </span>
      </button>

      {isOpen ? (
        <div className="region-selector-popover">
          <div className="region-selector-head">
            <strong>{TEXT.chooseCity}</strong>
            <span>{TEXT.current}</span>
          </div>

          <div className="region-selector-list">
            {knownRegions.map((option) => {
              const isActive = option.code === region.code;

              return (
                <button
                  className={`region-selector-option ${isActive ? "region-selector-option--active" : ""}`}
                  key={option.code}
                  onClick={() => {
                    setRegion(option);
                    setIsOpen(false);
                  }}
                  type="button"
                >
                  <strong>{fixMaybeBrokenText(option.label || option.city)}</strong>
                  <span>{fixMaybeBrokenText(option.area)}</span>
                </button>
              );
            })}
          </div>

          <div className="region-selector-foot">
            <span>{TEXT.pickup}</span>
            <Link href="/delivery" onClick={() => setIsOpen(false)}>
              {TEXT.details}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
